import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, FileText, Download, Eye, User, Edit, Trash2, RotateCcw } from 'lucide-react';
import { REPORTS } from '@/constants/testIds';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Avatar component for patient/therapist photos in reports
const PersonAvatar = ({ photoFileId, name, size = 'small' }) => {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPhoto = async () => {
      if (photoFileId) {
        try {
          const response = await axios.get(`${API}/files/${photoFileId}`, {
            withCredentials: true,
            responseType: 'blob'
          });
          const url = URL.createObjectURL(response.data);
          setPhotoUrl(url);
        } catch (error) {
          console.error('Error loading photo:', error);
        }
      }
      setLoading(false);
    };

    fetchPhoto();

    return () => {
      if (photoUrl) {
        URL.revokeObjectURL(photoUrl);
      }
    };
  }, [photoFileId]);

  const sizeClasses = size === 'small' ? 'w-8 h-8' : 'w-12 h-12';
  const iconSize = size === 'small' ? 'w-4 h-4' : 'w-6 h-6';

  if (loading && photoFileId) {
    return (
      <div className={`${sizeClasses} rounded-full bg-gray-200 animate-pulse flex-shrink-0`} />
    );
  }

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        className={`${sizeClasses} rounded-full object-cover flex-shrink-0 border-2 border-gray-200`}
        title={name}
      />
    );
  }

  return (
    <div className={`${sizeClasses} rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0`} title={name}>
      <User className={`${iconSize} text-gray-500`} />
    </div>
  );
};

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [patients, setPatients] = useState([]);
  const [therapists, setTherapists] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [trashedReports, setTrashedReports] = useState([]);
  
  const [formData, setFormData] = useState({
    patient_id: '',
    therapist_id: '',
    session_date: '',
    attendance: 'ASISTENTE',
    absence_reason: '',
    objective: '',
    duration_minutes: 60,
    resources_used: [],
    acceptance_metrics: {
      'Acepta el espacio': 'NO',
      'Acepta la propuesta': 'NO',
      'Rechaza la propuesta': 'NO',
      'Interactúa con el profesional': 'NO',
      'Sostiene actividad': 'NO',
      'Registro de salida': 'NO',
    },
    emotional_states: {
      ALERTA: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      SOMNOLIENTO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      ALEGRE: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      ENOJADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      CANSADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      RELAJADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      ANGUSTIADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      TRANQUILO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      IMPACIENTE: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      TENSO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      VERBORRÁGICO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
    },
    motor_skills: {
      TOCA: 'NO REALIZA',
      AGARRA: 'NO REALIZA',
      EXPLORA: 'NO REALIZA',
      ALCANZA: 'NO REALIZA',
      MANIPULA: 'NO REALIZA',
      COORDINA: 'NO REALIZA',
      'SEG. VISUAL': 'NO REALIZA',
    },
    processing_skills: {
      'INICIA TAREA': 'NO REALIZA',
      ATIENDE: 'NO REALIZA',
      ELIGE: 'NO REALIZA',
      'BUSCA/LOCALIZA OBJETOS': 'NO REALIZA',
      'LOCALIZA SONIDO': 'NO REALIZA',
    },
    social_skills: {
      'INICIA INTERACCIÓN': 'NO REALIZA',
      'CONCLUYE INTERACCIÓN': 'NO REALIZA',
      HABLA: 'NO REALIZA',
      GESTICULA: 'NO REALIZA',
      'PREGUNTA/RESPONDE': 'NO REALIZA',
      'EXPRESA EMOCIONES': 'NO REALIZA',
    },
    stimuli_applied: '',
    observations: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, patientsRes, therapistsRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/reports`, { withCredentials: true }),
        axios.get(`${API}/patients`, { withCredentials: true }),
        axios.get(`${API}/therapists`, { withCredentials: true }),
        axios.get(`${API}/resources`, { withCredentials: true }),
      ]);
      setReports(reportsRes.data);
      setPatients(patientsRes.data);
      setTherapists(therapistsRes.data);
      setResources(resourcesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingReport) {
        await axios.put(`${API}/reports/${editingReport.id}`, formData, { withCredentials: true });
        toast.success('Informe actualizado exitosamente');
      } else {
        await axios.post(`${API}/reports`, formData, { withCredentials: true });
        toast.success('Informe creado exitosamente');
      }
      setDialogOpen(false);
      resetForm();
      setEditingReport(null);
      fetchData();
    } catch (error) {
      console.error('Error saving report:', error);
      toast.error(error.response?.data?.detail || `Error al ${editingReport ? 'actualizar' : 'crear'} informe`);
    }
  };

  const resetForm = () => {
    setEditingReport(null);
    setFormData({
      patient_id: '',
      therapist_id: '',
      session_date: '',
      attendance: 'ASISTENTE',
      absence_reason: '',
      objective: '',
      duration_minutes: 60,
      resources_used: [],
      acceptance_metrics: {
        'Acepta el espacio': 'NO',
        'Acepta la propuesta': 'NO',
        'Rechaza la propuesta': 'NO',
        'Interactúa con el profesional': 'NO',
        'Sostiene actividad': 'NO',
        'Registro de salida': 'NO',
      },
      emotional_states: {
        ALERTA: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        SOMNOLIENTO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        ALEGRE: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        ENOJADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        CANSADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        RELAJADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        ANGUSTIADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        TRANQUILO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        IMPACIENTE: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        TENSO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        VERBORRÁGICO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      },
      motor_skills: {
        TOCA: 'NO REALIZA',
        AGARRA: 'NO REALIZA',
        EXPLORA: 'NO REALIZA',
        ALCANZA: 'NO REALIZA',
        MANIPULA: 'NO REALIZA',
        COORDINA: 'NO REALIZA',
        'SEG. VISUAL': 'NO REALIZA',
      },
      processing_skills: {
        'INICIA TAREA': 'NO REALIZA',
        ATIENDE: 'NO REALIZA',
        ELIGE: 'NO REALIZA',
        'BUSCA/LOCALIZA OBJETOS': 'NO REALIZA',
        'LOCALIZA SONIDO': 'NO REALIZA',
      },
      social_skills: {
        'INICIA INTERACCIÓN': 'NO REALIZA',
        'CONCLUYE INTERACCIÓN': 'NO REALIZA',
        HABLA: 'NO REALIZA',
        GESTICULA: 'NO REALIZA',
        'PREGUNTA/RESPONDE': 'NO REALIZA',
        'EXPRESA EMOCIONES': 'NO REALIZA',
      },
      stimuli_applied: '',
      observations: '',
    });
  };

  const handleDownloadPDF = async (reportId) => {
    try {
      const response = await axios.get(`${API}/reports/${reportId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `informe_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(error.response?.data?.detail || 'Error al descargar PDF');
    }
  };

  const handleEditReport = (report) => {
    setEditingReport(report);
    setFormData({
      patient_id: report.patient_id,
      therapist_id: report.therapist_id,
      session_date: report.session_date,
      attendance: report.attendance,
      absence_reason: report.absence_reason || '',
      objective: report.objective,
      duration_minutes: report.duration_minutes,
      resources_used: report.resources_used || [],
      acceptance_metrics: report.acceptance_metrics || {
        'Acepta el espacio': 'NO',
        'Acepta la propuesta': 'NO',
        'Rechaza la propuesta': 'NO',
        'Interactúa con el profesional': 'NO',
        'Sostiene actividad': 'NO',
        'Registro de salida': 'NO',
      },
      emotional_states: report.emotional_states || {
        ALERTA: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        SOMNOLIENTO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        ALEGRE: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        ENOJADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        CANSADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        RELAJADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        ANGUSTIADO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        TRANQUILO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        IMPACIENTE: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        TENSO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
        VERBORRÁGICO: { INGRESA: false, 'EN SESIÓN': false, 'SE RETIRA': false },
      },
      motor_skills: report.motor_skills || {},
      processing_skills: report.processing_skills || {},
      social_skills: report.social_skills || {},
      stimuli_applied: report.stimuli_applied || '',
      observations: report.observations || '',
    });
    setDialogOpen(true);
  };

  const handleDeleteReport = async (id) => {
    if (!window.confirm('¿Enviar este informe a la papelera? Podrás restaurarlo durante 30 días.')) return;
    try {
      await axios.delete(`${API}/reports/${id}`, { withCredentials: true });
      toast.success('Informe movido a la papelera');
      fetchData();
      if (showTrash) fetchTrash();
    } catch (error) {
      console.error('Error deleting report:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar informe');
    }
  };

  const fetchTrash = async () => {
    try {
      const { data } = await axios.get(`${API}/reports/trash`, { withCredentials: true });
      setTrashedReports(data || []);
    } catch (error) {
      setTrashedReports([]);
    }
  };

  const toggleTrash = () => {
    const next = !showTrash;
    setShowTrash(next);
    if (next) fetchTrash();
  };

  const handleRestoreReport = async (id) => {
    try {
      await axios.post(`${API}/reports/${id}/restore`, {}, { withCredentials: true });
      toast.success('Informe restaurado');
      fetchTrash();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restaurar');
    }
  };

  const handlePurgeReport = async (id) => {
    if (!window.confirm('¿Eliminar este informe DEFINITIVAMENTE? Esta acción no se puede deshacer.')) return;
    try {
      await axios.delete(`${API}/reports/${id}/purge`, { withCredentials: true });
      toast.success('Informe eliminado permanentemente');
      fetchTrash();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const percentageOptions = ['NO', '25-50%', '50-75%', '75-100%'];
  const skillLevels = ['CON APOYO TOTAL', 'CON APOYO PARCIAL', 'SIN APOYO', 'NO REALIZA'];

  if (loading) return <div>Cargando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Informes de Sesión
          </h1>
          <p className="text-sm mt-1" style={{ color: '#718096' }}>
            Gestiona los informes de sesiones multisensoriales
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showTrash ? 'default' : 'outline'}
            onClick={toggleTrash}
            className="flex items-center gap-2"
            data-testid="reports-trash-toggle"
          >
            <Trash2 size={18} />
            Papelera{showTrash && trashedReports.length ? ` (${trashedReports.length})` : ''}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
          <DialogTrigger asChild>
            <Button data-testid={REPORTS.createBtn} className="btn-primary flex items-center gap-2">
              <Plus size={20} />
              Nuevo Informe
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader className="bg-transparent sticky top-0 z-10 bg-white/95 backdrop-blur-sm pb-4 border-b border-gray-200">
              <DialogTitle className="text-2xl" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                {editingReport ? 'Editar Informe de Sesión' : 'Nuevo Informe de Sesión Multisensorial'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 pt-4">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-6 bg-gray-100">
                  <TabsTrigger 
                    value="basic"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#2D3748] text-[#718096]"
                  >
                    Datos Básicos
                  </TabsTrigger>
                  <TabsTrigger 
                    value="metrics"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#2D3748] text-[#718096]"
                  >
                    Métricas
                  </TabsTrigger>
                  <TabsTrigger 
                    value="emotional"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#2D3748] text-[#718096]"
                  >
                    Estados
                  </TabsTrigger>
                  <TabsTrigger 
                    value="skills"
                    className="data-[state=active]:bg-white data-[state=active]:text-[#2D3748] text-[#718096]"
                  >
                    Habilidades
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Paciente *</Label>
                      <select
                        value={formData.patient_id}
                        onChange={(e) => setFormData({ ...formData, patient_id: e.target.value })}
                        required
                        className="w-full mt-1 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
                      >
                        <option value="">Seleccionar paciente...</option>
                        {patients.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Terapeuta *</Label>
                      <select
                        value={formData.therapist_id}
                        onChange={(e) => setFormData({ ...formData, therapist_id: e.target.value })}
                        required
                        className="w-full mt-1 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
                      >
                        <option value="">Seleccionar terapeuta...</option>
                        {therapists.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Fecha de Sesión *</Label>
                      <Input
                        type="date"
                        value={formData.session_date}
                        onChange={(e) => setFormData({ ...formData, session_date: e.target.value })}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Asistencia *</Label>
                      <select
                        value={formData.attendance}
                        onChange={(e) => setFormData({ ...formData, attendance: e.target.value })}
                        className="w-full mt-1 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
                      >
                        <option value="ASISTENTE">Asistente</option>
                        <option value="AUSENTE">Ausente</option>
                      </select>
                    </div>
                    <div>
                      <Label>Duración (minutos) *</Label>
                      <Input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {formData.attendance === 'AUSENTE' && (
                    <div>
                      <Label>Motivo de Ausencia</Label>
                      <Input
                        value={formData.absence_reason}
                        onChange={(e) => setFormData({ ...formData, absence_reason: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Objetivo de la Sesión *</Label>
                    <Textarea
                      value={formData.objective}
                      onChange={(e) => setFormData({ ...formData, objective: e.target.value })}
                      required
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Recursos Utilizados en la Sesión</Label>
                    <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                      {resources.length === 0 ? (
                        <p className="text-sm text-gray-500">No hay recursos disponibles</p>
                      ) : (
                        resources.map((resource) => (
                          <div key={resource.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`resource-${resource.id}`}
                              checked={formData.resources_used.includes(resource.id)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setFormData({
                                    ...formData,
                                    resources_used: [...formData.resources_used, resource.id]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    resources_used: formData.resources_used.filter(id => id !== resource.id)
                                  });
                                }
                              }}
                            />
                            <Label htmlFor={`resource-${resource.id}`} className="text-sm font-normal cursor-pointer">
                              {resource.name} {resource.type && `(${resource.type})`}
                            </Label>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="metrics" className="space-y-4 mt-4">
                  <h3 className="font-semibold" style={{ color: '#2D3748' }}>Métricas de Aceptación</h3>
                  {Object.keys(formData.acceptance_metrics).map((metric) => (
                    <div key={metric} className="flex items-center justify-between">
                      <Label>{metric}</Label>
                      <select
                        value={formData.acceptance_metrics[metric]}
                        onChange={(e) => setFormData({
                          ...formData,
                          acceptance_metrics: { ...formData.acceptance_metrics, [metric]: e.target.value }
                        })}
                        className="w-48 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
                      >
                        {percentageOptions.map((opt) => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="emotional" className="space-y-4 mt-4">
                  <h3 className="font-semibold mb-4" style={{ color: '#2D3748' }}>Estados Emocionales Durante la Sesión</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#E5E2DC]">
                          <th className="text-left py-2 px-4">Estado</th>
                          <th className="text-center py-2 px-4">Ingresa</th>
                          <th className="text-center py-2 px-4">En Sesión</th>
                          <th className="text-center py-2 px-4">Se Retira</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.keys(formData.emotional_states).map((state) => (
                          <tr key={state} className="border-b border-[#E5E2DC]">
                            <td className="py-2 px-4">{state}</td>
                            {['INGRESA', 'EN SESIÓN', 'SE RETIRA'].map((moment) => (
                              <td key={moment} className="text-center py-2 px-4">
                                <Checkbox
                                  checked={formData.emotional_states[state][moment]}
                                  onCheckedChange={(checked) => setFormData({
                                    ...formData,
                                    emotional_states: {
                                      ...formData.emotional_states,
                                      [state]: { ...formData.emotional_states[state], [moment]: checked }
                                    }
                                  })}
                                />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TabsContent>

                <TabsContent value="skills" className="space-y-6 mt-4">
                  <div>
                    <h3 className="font-semibold mb-3" style={{ color: '#2D3748' }}>Habilidades Motoras</h3>
                    {Object.keys(formData.motor_skills).map((skill) => (
                      <div key={skill} className="flex items-center justify-between mb-2">
                        <Label>{skill}</Label>
                        <select
                          value={formData.motor_skills[skill]}
                          onChange={(e) => setFormData({
                            ...formData,
                            motor_skills: { ...formData.motor_skills, [skill]: e.target.value }
                          })}
                          className="w-64 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
                        >
                          {skillLevels.map((level) => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3" style={{ color: '#2D3748' }}>Habilidades de Procesamiento</h3>
                    {Object.keys(formData.processing_skills).map((skill) => (
                      <div key={skill} className="flex items-center justify-between mb-2">
                        <Label>{skill}</Label>
                        <select
                          value={formData.processing_skills[skill]}
                          onChange={(e) => setFormData({
                            ...formData,
                            processing_skills: { ...formData.processing_skills, [skill]: e.target.value }
                          })}
                          className="w-64 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
                        >
                          {skillLevels.map((level) => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div>
                    <h3 className="font-semibold mb-3" style={{ color: '#2D3748' }}>Habilidades Sociales</h3>
                    {Object.keys(formData.social_skills).map((skill) => (
                      <div key={skill} className="flex items-center justify-between mb-2">
                        <Label>{skill}</Label>
                        <select
                          value={formData.social_skills[skill]}
                          onChange={(e) => setFormData({
                            ...formData,
                            social_skills: { ...formData.social_skills, [skill]: e.target.value }
                          })}
                          className="w-64 bg-white border border-[#E5E2DC] rounded-lg px-4 py-2"
                        >
                          {skillLevels.map((level) => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <div>
                    <Label>Estímulos Aplicados *</Label>
                    <Textarea
                      value={formData.stimuli_applied}
                      onChange={(e) => setFormData({ ...formData, stimuli_applied: e.target.value })}
                      required
                      rows={3}
                      className="mt-1"
                      placeholder="Describe los estímulos utilizados durante la sesión..."
                    />
                  </div>

                  <div>
                    <Label>Observaciones *</Label>
                    <Textarea
                      value={formData.observations}
                      onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
                      required
                      rows={4}
                      className="mt-1"
                      placeholder="Observaciones generales de la sesión..."
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <Button type="submit" data-testid={REPORTS.saveBtn} className="btn-primary w-full">
                Crear Informe
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {showTrash && (
        <Card className="p-6 border-amber-200" data-testid="reports-trash-panel">
          <div className="flex items-center gap-2 mb-1">
            <Trash2 className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-amber-800">Papelera de Informes ({trashedReports.length})</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Los informes eliminados se conservan 30 días y luego se borran automáticamente.
          </p>
          {trashedReports.length === 0 ? (
            <p className="text-sm text-gray-500">No hay informes en la papelera.</p>
          ) : (
            <div className="space-y-2">
              {trashedReports.map((r) => {
                const patient = patients.find(p => p.id === r.patient_id);
                return (
                  <div key={r.id} className="flex items-center justify-between border rounded-lg px-4 py-3" data-testid={`trash-report-${r.id}`}>
                    <div>
                      <p className="font-medium" style={{ color: '#2D3748' }}>
                        {patient?.name || 'Paciente'} — {r.session_date}
                      </p>
                      <p className="text-xs text-gray-500">{r.days_remaining ?? 30} días restantes</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => handleRestoreReport(r.id)}
                        data-testid={`restore-report-${r.id}`}
                      >
                        <RotateCcw className="h-4 w-4 mr-1" />
                        Restaurar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handlePurgeReport(r.id)}
                        data-testid={`purge-report-${r.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4" data-testid={REPORTS.list}>
        {reports.map((report) => {
          const patient = patients.find(p => p.id === report.patient_id);
          const therapist = therapists.find(t => t.id === report.therapist_id);
          
          return (
            <Card key={report.id} className="p-6 hover:-translate-y-1 transition-transform duration-200">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <PersonAvatar 
                      photoFileId={patient?.photo_file_id} 
                      name={patient?.name || 'Paciente'} 
                      size="medium"
                    />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                        {patient?.name || 'Paciente no encontrado'}
                      </h3>
                      <div className="flex items-center gap-2 text-sm" style={{ color: '#718096' }}>
                        <span>Sesión del {report.session_date}</span>
                        <span>•</span>
                        <div className="flex items-center gap-1">
                          <span>Terapeuta:</span>
                          <PersonAvatar 
                            photoFileId={therapist?.photo_file_id} 
                            name={therapist?.name || 'N/A'} 
                            size="small"
                          />
                          <span>{therapist?.name || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-xs font-medium" style={{ color: '#718096' }}>Asistencia</p>
                      <p className="text-sm" style={{ color: '#2D3748' }}>{report.attendance}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium" style={{ color: '#718096' }}>Duración</p>
                      <p className="text-sm" style={{ color: '#2D3748' }}>{report.duration_minutes} min</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium" style={{ color: '#718096' }}>Objetivo</p>
                      <p className="text-sm truncate" style={{ color: '#2D3748' }}>{report.objective}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditReport(report)}
                    className="hover:bg-[#E5E2DC]"
                    title="Editar informe"
                  >
                    <Edit size={16} style={{ color: '#5A7D6C' }} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteReport(report.id)}
                    className="hover:bg-red-50"
                    title="Eliminar informe"
                  >
                    <Trash2 size={16} style={{ color: '#F44336' }} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownloadPDF(report.id)}
                    data-testid={REPORTS.exportPdfBtn}
                    className="flex items-center gap-2"
                  >
                    <Download size={16} />
                    PDF
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsPage;
